import './ControllerDevPage.scss';

import { FilePicker } from '@capawesome/capacitor-file-picker';
import {
  IonButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCol,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonProgressBar,
  IonRow,
  useIonAlert,
} from '@ionic/react';
import { addCircle, refresh, trash, chevronDown, chevronUp } from 'ionicons/icons';
import moment from 'moment';
import React from 'react';
import { useSelector } from 'react-redux';
import { BleService } from 'src/ble/BleService';
import { DqText } from 'src/components/dq-text';
import MobileHeader from 'src/components/reusable-components/MobileHeader';
import {
  selectBleBundleFiles,
  selectBleConnectedDevice,
  selectBleIsLocked,
  selectBleRemoteLogFiles,
  selectBleWriteLog,
} from 'src/redux/ble/selectors';
import { store } from 'src/redux/store';
import { ControllerDevLogFileViewer } from './log-file-viewer/ControllerDevLogFileViewer';

export const ControllerDevPage: React.FC = () => {
  const [alert] = useIonAlert();
  const [isLogListVisible, setLogListVisible] = React.useState(false);
  const [isBundleListVisible, setBundleListVisible] = React.useState(true);
  const [selectedLog, setSelectedLog] = React.useState<string | undefined>(undefined);
  const lock = useSelector(selectBleIsLocked);
  const device = useSelector(selectBleConnectedDevice);
  const writeLogState = useSelector(selectBleWriteLog);
  const unsortedLogs = useSelector(selectBleRemoteLogFiles);
  const unsortedBundles = useSelector(selectBleBundleFiles);

  const quickConnect = React.useCallback(async () => {
    const abort = new AbortController();
    const interval = window.setInterval(() => {
      const devices = store.getState().ble.connectableDevices;
      if (devices[0]) {
        abort.abort();
        window.clearInterval(interval);
      }
    }, 125);
    await BleService.scan(abort.signal);
    const devices = store.getState().ble.connectableDevices;
    const first = devices[0];
    if (!first) {
      console.error('no device found!');
      return;
    }
    await BleService.connect(first);
  }, []);

  const logs = React.useMemo(() => {
    return [...unsortedLogs].sort((a, b) => {
      if (a.createdAt > b.createdAt) return -1;
      if (a.createdAt < b.createdAt) return 1;
      return 0;
    });
  }, [unsortedLogs]);

  const bundles = React.useMemo(() => {
    return [...unsortedBundles].sort((a, b) => {
      if (a.createdAt > b.createdAt) return -1;
      if (a.createdAt < b.createdAt) return 1;
      return 0;
    });
  }, [unsortedBundles]);

  const writeLogProgress = React.useMemo(() => {
    if (!writeLogState) return undefined;
    return writeLogState.writtenBytes / writeLogState.totalBytes;
  }, [writeLogState]);

  const onDeleteLogClick = React.useCallback(
    async (filePath: string) => {
      await alert('Are you sure you want to delete this log file?', [
        {
          text: 'Yes',
          role: 'confirm',
          handler: async () => {
            await BleService.controller?.deleteLog?.(filePath);
            await BleService.controller?.listLogFiles?.();
          },
        },
        { text: 'Cancel', role: 'cancel' },
      ]);
    },
    [alert],
  );

  const onDeleteBundleClick = React.useCallback(
    async (bundleId: string) => {
      await alert('Are you sure you want to delete this bundle file?', [
        {
          text: 'Yes',
          role: 'confirm',
          handler: async () => {
            await BleService.controller?.deleteBundle?.(bundleId);
          },
        },
        { text: 'Cancel', role: 'cancel' },
      ]);
    },
    [alert],
  );

  const onAddLogClick = React.useCallback(async () => {
    try {
      const result = await FilePicker.pickFiles({ types: undefined, readData: true });
      const file = result.files[0];
      if (!file || !file?.data) return;
      await BleService.controller?.writeLog?.(file.name, window.atob(file.data));
      await BleService.controller?.listLogFiles?.();
    } catch (e) {
      console.warn('failed to add log', e);
    }
  }, []);

  const onDeleteAllBundlesClick = React.useCallback(async () => {
    await alert('Are you sure you want to delete ALL bundle files?', [
      {
        text: 'Yes',
        role: 'confirm',
        handler: async () => {
          for (const bundle of bundles) {
            await BleService.controller?.deleteBundle?.(bundle.bundleId);
          }
        },
      },
      { text: 'Cancel', role: 'cancel' },
    ]);
  }, [alert, bundles]);

  return (
    <IonPage className="ctrl-dev-page">
      <MobileHeader title="Controller Dev Control" />
      <IonContent>
        <IonCard className="ion-margin-bottom">
          <IonCardHeader>
            <IonCardTitle>
              <DqText variant="h3">Controller</DqText>
            </IonCardTitle>
            <IonButton fill="clear" size="small" disabled={lock} onClick={quickConnect}>
              <IonIcon slot="icon-only" icon={refresh} />
            </IonButton>
          </IonCardHeader>
          {!device ? (
            <IonList>
              <IonItem>
                <DqText>No Controller Connected</DqText>
                <IonButton slot="end" onClick={quickConnect} disabled={lock}>
                  Connect
                </IonButton>
              </IonItem>
            </IonList>
          ) : (
            <IonList>
              <IonItem>
                <IonLabel>
                  <IonLabel position="stacked">
                    <DqText color="medium" variant="label">
                      Id
                    </DqText>
                  </IonLabel>
                  <DqText>{device.deviceId}</DqText>
                </IonLabel>
              </IonItem>
              {device.name && (
                <IonItem>
                  <IonLabel>
                    <IonLabel position="stacked">
                      <DqText color="medium" variant="label">
                        Name
                      </DqText>
                    </IonLabel>
                    <DqText>{device.name}</DqText>
                  </IonLabel>
                </IonItem>
              )}
              <IonItem>
                <IonLabel>
                  <IonLabel position="stacked">
                    <DqText color="medium" variant="label">
                      Serial Number
                    </DqText>
                  </IonLabel>
                  <DqText>{device.serialNumber}</DqText>
                </IonLabel>
              </IonItem>
              <IonItem>
                <IonLabel>
                  <IonLabel position="stacked">
                    <DqText color="medium" variant="label">
                      Firmware Version
                    </DqText>
                  </IonLabel>
                  <DqText>{device.firmwareVersion}</DqText>
                </IonLabel>
              </IonItem>
            </IonList>
          )}
        </IonCard>
        <IonCard className="ion-margin-bottom">
          <IonCardHeader>
            <IonCardTitle>
              <IonButton fill="clear" className="ion-no-padding" onClick={() => setLogListVisible(!isLogListVisible)}>
                <DqText variant="h3">Logs</DqText>
                <IonIcon slot="end" icon={isLogListVisible ? chevronDown : chevronUp} />
              </IonButton>
            </IonCardTitle>
            <div>
              <IonButton fill="clear" size="small" disabled={lock || !device} onClick={onAddLogClick}>
                <IonIcon slot="icon-only" icon={addCircle} />
              </IonButton>
              <IonButton
                fill="clear"
                size="small"
                disabled={lock || !device}
                onClick={() => BleService.controller?.listLogFiles()}
              >
                <IonIcon slot="icon-only" icon={refresh} />
              </IonButton>
            </div>
          </IonCardHeader>
          {writeLogProgress !== undefined && <IonProgressBar value={writeLogProgress} />}
          {isLogListVisible && (
            <IonList>
              {logs.map(log => {
                return (
                  <IonItem key={log.fileName}>
                    <IonLabel onClick={() => setSelectedLog(log.fileName)}>
                      <IonRow>
                        <IonCol>
                          <DqText>{log.fileName}</DqText>
                        </IonCol>
                        <IonCol>
                          <DqText>{log.fileSize}B</DqText>
                        </IonCol>
                      </IonRow>
                      <IonRow>
                        <IonCol />
                        <IonCol>
                          <DqText>{moment(log.createdAt).format('L')}</DqText>
                        </IonCol>
                      </IonRow>
                    </IonLabel>
                    <div slot="end">
                      <IonButton fill="clear" disabled={lock} onClick={() => onDeleteLogClick(log.fileName)}>
                        <IonIcon slot="icon-only" icon={trash} />
                      </IonButton>
                    </div>
                  </IonItem>
                );
              })}
            </IonList>
          )}
        </IonCard>
        <IonCard className="ion-margin-bottom">
          <IonCardHeader>
            <IonCardTitle>
              <IonButton
                fill="clear"
                className="ion-no-padding"
                onClick={() => setBundleListVisible(!isBundleListVisible)}
              >
                <DqText variant="h3">Bundles</DqText>
                <IonIcon slot="end" icon={isBundleListVisible ? chevronDown : chevronUp} />
              </IonButton>
            </IonCardTitle>
            <div>
              <IonButton fill="clear" size="small" disabled={lock || !device} onClick={onDeleteAllBundlesClick}>
                <IonIcon slot="icon-only" icon={trash} />
              </IonButton>
              <IonButton
                fill="clear"
                size="small"
                disabled={lock || !device}
                onClick={() => BleService.controller?.listBundles()}
              >
                <IonIcon slot="icon-only" icon={refresh} />
              </IonButton>
            </div>
          </IonCardHeader>
          {isBundleListVisible && (
            <IonList>
              {bundles.map(bundle => {
                return (
                  <IonItem key={bundle.bundleId}>
                    <IonLabel>
                      <IonRow>
                        <IonCol>
                          <DqText>{bundle.fileName}</DqText>
                        </IonCol>
                        <IonCol>
                          <DqText>{bundle.fileSize}B</DqText>
                        </IonCol>
                      </IonRow>
                      <IonRow>
                        <IonCol>
                          <DqText>{bundle.siteName}</DqText>
                        </IonCol>
                        <IonCol>
                          <DqText>{moment(bundle.createdAt).format('L')}</DqText>
                        </IonCol>
                      </IonRow>
                    </IonLabel>
                    <div slot="end">
                      <IonButton fill="clear" disabled={lock} onClick={() => onDeleteBundleClick(bundle.bundleId)}>
                        <IonIcon slot="icon-only" icon={trash} />
                      </IonButton>
                    </div>
                  </IonItem>
                );
              })}
            </IonList>
          )}
        </IonCard>
      </IonContent>
      <ControllerDevLogFileViewer filename={selectedLog} onClose={() => setSelectedLog(undefined)} />
    </IonPage>
  );
};
